import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./Chatbot.css";
import { IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

const Chatbot = () => {
  const [apiKey, setApiKey] = useState("");
  const [messages, setMessages] = useState<{ role: string; content: string }[]>(
    []
  );
  const [input, setInput] = useState("");
  const [models, setModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>("gemini-1.0-pro");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (apiKey) {
      fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
      )
        .then((response) => response.json())
        .then((data) => {
          const filteredModels = data.models.filter((model: any) =>
            model.supportedGenerationMethods.includes("generateContent")
          );
          setModels(filteredModels);
          if (filteredModels.length > 0) {
            setSelectedModel(filteredModels[0].name);
          }
        });
    }
  }, [apiKey]);

  const handleSend = async () => {
    if (!input.trim() || !apiKey) return;

    const newMessages = [...messages, { role: "user", content: input }];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/${selectedModel}:generateContent?key=${apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contents: newMessages.map((msg) => ({
              role: msg.role === "assistant" ? "model" : "user",
              parts: [{ text: msg.content }],
            })),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch from Gemini API");
      }

      const data = await response.json();
      const assistantMessage = {
        role: "model",
        content: data.candidates[0].content.parts[0].text,
      };

      setMessages([...newMessages, assistantMessage]);
    } catch (error) {
      console.error(error);
      setMessages([
        ...newMessages,
        { role: "model", content: "Sorry, something went wrong." },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chatbot-container">
      <div className="api-key-container">
        {!apiKey && (
          <input
            type="password"
            className="api-key-input"
            placeholder="Enter your Gemini API Key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        )}
        {models.length > 0 && (
          <select
            className="model-selector"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
          >
            {models.map((model) => (
              <option key={model.name} value={model.name}>
                {model.displayName}
              </option>
            ))}
          </select>
        )}
      </div>
      {!apiKey && (
        <div style={{ padding: "8px", maxHeight: "100%", overflow: "auto" }}>
          <p>
            <b>
              Disclaimer: AI answer is not always accurate. Please use it at
              your own risk.
            </b>
          </p>
          <p>
            <b>Note: </b>We don't use or store your API key anywhere. This app
            will directly connect with Gemini AI and answer your queries
            directly.
          </p>
          <p>
            <b>Steps to Get an API Key</b>
          </p>
          <p>
            1. <b>Go to Google AI Studio:</b> Open the Google AI Studio API Keys
            page.
          </p>
          <p>
            <a href="https://aistudio.google.com/app/api-keys">
              https://aistudio.google.com/app/api-keys
            </a>
          </p>
          <p>
            2. <b>Sign in:</b> Use a Google Account. If there isn't one, create
            an account.
          </p>
          <p>
            3. <b>Accept Terms:</b> First-time users must review and accept the
            Terms of Service and data usage policies.
          </p>
          <p>
            4. <b>Create an API Key:</b>
            <ol>
              <li>Click the Create API key button on the "API keys" page.</li>
              <li>
                Choose to create a key in a new Google Cloud project or use an
                existing one. Creating a key in a new project is best for most
                users.
              </li>
              <li>Choose an option and click Create API key.</li>
            </ol>
          </p>
          <p>
            5. <b>Copy the Key:</b>
          </p>{" "}
          The API key will appear immediately. Copy the key and keep it safe.
          The key usually starts with "AIza".
        </div>
      )}
      {apiKey && (
        <div className="messages-container">
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.role}`}>
              {msg.role === "model" ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.content}
                </ReactMarkdown>
              ) : (
                msg.content
              )}
            </div>
          ))}
          {isLoading && (
            <div className="loading-indicator">Fetching AI response...</div>
          )}
        </div>
      )}
      {apiKey && (
        <div className="input-container">
          <textarea
            className="input-field"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={2}
          />
          <IconButton color="primary" onClick={handleSend}>
            <SendIcon />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
